/**
 * Colour-scheme variants of the skinned garments.
 *
 * A garment GLB carries no texture: it is two or three flat-colour materials
 * (a main, a darker shade, a trim). A "colour scheme" is therefore just a
 * different colour for those materials, so one mesh file serves every variant
 * and each variant is only a row here plus a separate ItemDef in the seed
 * (`backend/prisma/content/garment-variants.ts`; `garment-variants.test.ts`
 * holds the two in step).
 *
 * Variant keys are `<base>_<id>` (`torso_jacket_navy`). The loader resolves a
 * variant to its base file (`garmentBaseKey`) and recolours a per-instance
 * copy of the materials, never the cached ones.
 */

/** glTF linear-RGB triple, exactly what `baseColorFactor` stores. */
export type Rgb = readonly [number, number, number];

export interface GarmentVariant {
  readonly base: string;
  readonly id: string;
  /** Item-name suffix: `Field Jacket (Navy)`. */
  readonly label: string;
  /** The main colour as sRGB hex, for icons and tooling. */
  readonly hex: string;
  /** Material name -> linear colour, only for the materials that change. */
  readonly colors: Readonly<Record<string, Rgb>>;
}

/** Which material plays which part in each base garment, from the shipped GLBs. */
interface Roles {
  readonly main: string;
  /** Darker shade, derived from the main colour. Absent where the dark part is a fixed detail. */
  readonly dark?: string;
  /** A second colour a scheme may set itself. Left as shipped when a scheme gives none. */
  readonly accent?: string;
}

const ROLES: Readonly<Record<string, Roles>> = {
  torso_jacket: { main: 'g_olive', dark: 'g_olive_dark' },
  torso_leather: { main: 'g_leather', dark: 'g_leather_dk' },
  // The black stripes of the plaid are part of the pattern and stay as shipped.
  torso_lumbershirt: { main: 'g_plaid_red' },
  torso_puffer: { main: 'g_puffer', dark: 'g_puffer_dk' },
  'torso_tactical-hoodie': { main: 'g_hoodie', dark: 'g_hoodie_dk' },
  torso_trench: { main: 'g_trench', dark: 'g_trench_dk' },
  torso_raincoat: { main: 'g_rain', dark: 'g_rain_dk' },
  legs_jeans: { main: 'g_denim', dark: 'g_denim_dk' },
  legs_tactical: { main: 'g_tacgrey', dark: 'g_tacgrey_dk' },
  legs_cargo: { main: 'g_olive', dark: 'g_olive_dark' },
  'legs_shorts-cargo': { main: 'g_olive', dark: 'g_olive_dark' },
  'legs_sport-short': { main: 'g_sport', accent: 'g_sport_teal' },
  glove_mitten: { main: 'g_mitten' },
  feet_boots: { main: 'g_boot', dark: 'g_boot_sole' },
};

/** The shipped dark shades are 0.6 of their main colour in linear space. */
const DARK_SHADE = 0.6;

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function hexToLinear(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [srgbToLinear((n >> 16) & 255), srgbToLinear((n >> 8) & 255), srgbToLinear(n & 255)];
}

interface Scheme {
  readonly id: string;
  readonly label: string;
  readonly main: string;
  readonly accent?: string;
}

const s = (id: string, label: string, main: string, accent?: string): Scheme => ({ id, label, main, accent });

/** Faded wasteland dyes. Five per garment, three for the coats. */
const SCHEMES: Readonly<Record<string, readonly Scheme[]>> = {
  torso_jacket: [
    s('navy', 'Navy', '#44557a'), s('sand', 'Sand', '#b8a577'), s('rust', 'Rust', '#9a5636'),
    s('charcoal', 'Charcoal', '#4a4d52'), s('forest', 'Forest', '#46663e'),
  ],
  torso_leather: [
    s('black', 'Black', '#35312f'), s('oxblood', 'Oxblood', '#6a2f33'), s('slate', 'Slate', '#56657a'),
    s('olive', 'Olive', '#5a6a2e'), s('bone', 'Bone', '#c9bea3'),
  ],
  torso_lumbershirt: [
    s('blue', 'Blue', '#4a6a9c'), s('green', 'Green', '#53804e'), s('mustard', 'Mustard', '#c19a3a'),
    s('grey', 'Grey', '#7c8084'), s('plum', 'Plum', '#7a4a74'),
  ],
  torso_puffer: [
    s('orange', 'Orange', '#cf6a2d'), s('navy', 'Navy', '#34497a'), s('olive', 'Olive', '#77803f'),
    s('bone', 'Bone', '#cbc2a8'), s('oxblood', 'Oxblood', '#7a2d33'),
  ],
  'torso_tactical-hoodie': [
    s('black', 'Black', '#3a3b3e'), s('forest', 'Forest', '#4b6a43'), s('maroon', 'Maroon', '#7a3036'),
    s('navy', 'Navy', '#3b4e78'), s('sand', 'Sand', '#bba97e'),
  ],
  torso_trench: [
    s('black', 'Black', '#3c3b3d'), s('olive', 'Olive', '#66693f'), s('oxblood', 'Oxblood', '#6c3034'),
  ],
  torso_raincoat: [
    s('yellow', 'Yellow', '#d3ab2e'), s('navy', 'Navy', '#36497a'), s('olive', 'Olive', '#6a7340'),
  ],
  legs_jeans: [
    s('black', 'Black', '#3a3b40'), s('indigo', 'Indigo', '#34467a'), s('stonewash', 'Stonewash', '#a3b6cc'),
    s('sand', 'Sand', '#b9a77c'), s('rust', 'Rust', '#9a5a3a'),
  ],
  legs_tactical: [
    s('black', 'Black', '#3b3c3e'), s('olive', 'Olive', '#66693f'), s('desert', 'Desert', '#b6a479'),
    s('navy', 'Navy', '#3f5078'), s('brown', 'Brown', '#6b4f38'),
  ],
  legs_cargo: [
    s('khaki', 'Khaki', '#b0a272'), s('black', 'Black', '#3b3c3d'), s('navy', 'Navy', '#3f5078'),
    s('brown', 'Brown', '#6b4f38'), s('teal', 'Teal', '#3f6f6a'),
  ],
  'legs_shorts-cargo': [
    s('khaki', 'Khaki', '#b0a272'), s('navy', 'Navy', '#3f5078'), s('charcoal', 'Charcoal', '#484a4e'),
    s('rust', 'Rust', '#9a5636'), s('bone', 'Bone', '#dbd3bd'),
  ],
  'legs_sport-short': [
    s('navy', 'Navy', '#2f4375', '#e0a82e'), s('red', 'Red', '#a23a32', '#e8e1d0'),
    s('forest', 'Forest', '#3b5c3d', '#d6c24c'), s('grey', 'Grey', '#8a8d90', '#2d6f8f'),
    s('bone', 'Bone', '#cdc5ad', '#a23a32'),
  ],
  glove_mitten: [
    s('red', 'Red', '#a23a32'), s('navy', 'Navy', '#34497a'), s('mustard', 'Mustard', '#c19a3a'),
    s('green', 'Green', '#4e7a48'), s('cream', 'Cream', '#cbc2a8'),
  ],
  feet_boots: [
    s('black', 'Black', '#1f1e20'), s('brown', 'Brown', '#6e4d34'), s('tan', 'Tan', '#a68257'),
    s('olive', 'Olive', '#565c3a'), s('grey', 'Grey', '#8c8f94'),
  ],
};

function build(base: string, scheme: Scheme): GarmentVariant {
  const roles = ROLES[base]!;
  const main = hexToLinear(scheme.main);
  const colors: Record<string, Rgb> = { [roles.main]: main };
  if (roles.dark) colors[roles.dark] = main.map((c) => c * DARK_SHADE) as unknown as Rgb;
  if (roles.accent && scheme.accent) colors[roles.accent] = hexToLinear(scheme.accent);
  return { base, id: scheme.id, label: scheme.label, hex: scheme.main, colors };
}

export function garmentVariantKey(base: string, id: string): string {
  return `${base}_${id}`;
}

export const GARMENT_VARIANTS: Readonly<Record<string, GarmentVariant>> = Object.fromEntries(
  Object.entries(SCHEMES).flatMap(([base, schemes]) =>
    schemes.map((scheme) => [garmentVariantKey(base, scheme.id), build(base, scheme)] as const)),
);

export const GARMENT_VARIANT_KEYS: readonly string[] = Object.keys(GARMENT_VARIANTS);

/** Garments that have variants, with their shipped roles — for tooling and tests. */
export const GARMENT_ROLES = ROLES;

/** The garment whose mesh and rules a key uses: itself, or the base of a variant. */
export function garmentBaseKey(key: string): string {
  return GARMENT_VARIANTS[key]?.base ?? key;
}

/** Material colour overrides for a variant, or null for a base garment. */
export function garmentColors(key: string): Readonly<Record<string, Rgb>> | null {
  return GARMENT_VARIANTS[key]?.colors ?? null;
}
